'use client';

import { StudioAction } from '@/components/ui/StudioAction';

import { SystemLoadingSpinner } from '@/components/ui/SystemLoading';
import styles from './Commerce.module.css';
import { useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { replaceCreditUnitLabel } from '@/lib/credits/unit';
import { resolvePaymentReturnDestination } from '@/lib/payment-return-routing';
import { useCreditsStore } from '@/lib/stores/credits-store';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // 最多轮询 60 秒

/**
 * 支付回调处理器 — 从支付宝跳回后轮询订单状态。
 *
 * 挂载在 /payment-return 中，检测 URL 参数 ?order_id=xxx。
 *
 * ★ 关键：从支付宝跳回时整个 SPA 已被销毁重建，内存中的 access_token 为空。
 * 必须先通过 ensureValidToken() 用 HttpOnly cookie 恢复 token，再开始轮询，
 * 否则首次请求会因无 token 而 401。
 */
type PaymentReturnHandlerProps = {
  orderId?: string | null;
  returnTo?: string | null;
  showStatus?: boolean;
};

export function PaymentReturnHandler({
  orderId: providedOrderId,
  returnTo: providedReturnTo,
  showStatus = false,
}: PaymentReturnHandlerProps = {}) {
  const router = useRouter();
  const searchParams = useSearchParams()!;
  const t = useTranslations('pricing');
  const locale = useLocale();
  const pollingRef = useRef(false);

  useEffect(() => {
    const orderId = providedOrderId ?? searchParams.get('order_id');
    if (!orderId || pollingRef.current) return;

    pollingRef.current = true;
    let pollCount = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    let terminal = false;

    const returnTo = providedReturnTo ?? searchParams.get('return_to');
    const finish = (
      outcome: Parameters<typeof resolvePaymentReturnDestination>[0],
    ) => {
      router.replace(resolvePaymentReturnDestination(outcome, orderId, returnTo), { scroll: false });
      pollingRef.current = false;
      terminal = true;
    };

    const startPolling = async () => {
      const [
        { toast },
        { subscriptionsApi },
        { ensureValidToken, useAuthStore },
      ] = await Promise.all([
        import('@/lib/stores/toast-store'),
        import('@/lib/api/subscriptions'),
        import('@/stores/authStore'),
      ]);

      if (cancelled) return;
      let requestUserId: string | null = null;
      const handle = toast.persistent(t('paymentProcessing'));

      const scheduleNext = (poll: () => Promise<void>) => {
        if (!cancelled && !terminal) {
          timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
        }
      };

      const poll = async () => {
        if (cancelled || terminal) return;
        pollCount++;
        try {
          const res = await subscriptionsApi.getOrderStatus(orderId);

          if (cancelled) return;
          if ((useAuthStore.getState().user?.id ?? null) !== requestUserId) {
            handle.dismiss();
            finish('auth_expired');
            return;
          }

          // API client 返回 { error } 时说明请求失败（401/网络错误等）
          if (res.error) {
            if (res.error.code === 'UNAUTHORIZED') {
              // token 恢复也失败了 — 登录态确实过期
              handle.dismiss();
              toast.error(t('paymentAuthExpired'));
              finish('auth_expired');
              return;
            }
            if (res.error.code.startsWith('HTTP_4')) {
              // 4xx 为确定性错误（如订单不存在 / 无权限），无需继续轮询
              handle.dismiss();
              toast.showError(res.error, 'payment', { fallbackMessage: t('paymentOrderUnavailable') });
              finish('unavailable');
              return;
            }
            // 其他错误继续重试，直到超时
            if (pollCount >= MAX_POLLS) {
              handle.dismiss();
              toast.error(t('paymentTimeout'));
              finish('timeout');
            } else {
              scheduleNext(poll);
            }
            return;
          }

          const data = res.data!;
          if (data.status === 'paid') {
            handle.dismiss();

            if (data.order_type === 'topup') {
              const credits = data.total_credits;
              toast.success(replaceCreditUnitLabel(
                t('topUpSuccess', { totalCredits: credits ? credits.toLocaleString() : '' }),
                data.credit_unit || data || useCreditsStore.getState().credits,
                locale,
              ));
            } else {
              toast.success(t('subscriptionSuccess'));
            }
            const paymentEvent = {
              order_id: orderId,
              order_type: data.order_type,
              plan_slug: data.plan_slug,
              amount_yuan: data.amount_yuan,
              total_credits: data.total_credits,
            };
            // 已支付是权威终态，先清理回跳 URL，任何本地刷新/归因异常都不能把用户困在轮询页。
            if (!cancelled) {
              finish('paid');
            }

            try {
              const { useFeatureFlagsStore } = await import('@/lib/stores/feature-flags-store');
              await Promise.allSettled([
                useCreditsStore.getState().refetchCredits(),
                useFeatureFlagsStore.getState().refetchFlags(),
              ]);
            } catch (error) {
              console.warn('[PaymentReturn] Failed to refresh local commercial state:', error);
            }
            if ((useAuthStore.getState().user?.id ?? null) === requestUserId) {
              window.dispatchEvent(new CustomEvent('dobidobi:subscription-updated', {
                detail: paymentEvent,
              }));
            }

            try {
              const { trackSocialPublishEventIfAttributed } = await import('@/lib/social-publishing/attribution');
              await trackSocialPublishEventIfAttributed('paid', {
                ...paymentEvent,
              });
            } catch (error) {
              console.warn('[SocialPublishing] Failed to track paid event:', error);
            }
            try {
              const { trackWebMarketingEventIfAttributed } = await import('@/lib/marketing/attribution');
              await trackWebMarketingEventIfAttributed('paid', {
                order_id: orderId,
                order_type: data.order_type,
                plan_slug: data.plan_slug,
                amount_yuan: data.amount_yuan,
                total_credits: data.total_credits,
              });
            } catch (error) {
              console.warn('[WebMarketing] Failed to track paid event:', error);
            }
            useCreditsStore.getState().refetchCredits();
          } else if (data.status === 'expired') {
            // 订单已过期（超时未支付）
            handle.dismiss();
            toast.error(t('paymentExpired'));
            finish('expired');
          } else if (data.status === 'amount_mismatch') {
            handle.dismiss();
            toast.error(t('paymentAmountMismatch'));
            finish('amount_mismatch');
          } else if (data.status === 'limit_rejected') {
            handle.dismiss();
            toast.error(t('topupLimitRaceRejected'));
            finish('limit_rejected');
          } else if (data.status === 'refunded') {
            handle.dismiss();
            toast.error(t('paymentRefundConfirmed'));
            finish('refunded');
          } else if (data.status === 'resolved') {
            handle.dismiss();
            toast.info(t('paymentManuallyResolved'));
            finish('resolved');
          } else if (data.status === 'ignored') {
            handle.dismiss();
            toast.error(t('paymentPermanentlyIgnored'));
            finish('ignored');
          } else if (pollCount >= MAX_POLLS) {
            handle.dismiss();
            toast.error(t('paymentTimeout'));
            finish('timeout');
          } else {
            scheduleNext(poll);
          }
        } catch {
          if (cancelled) return;
          if (pollCount >= MAX_POLLS) {
            handle.dismiss();
            toast.error(t('paymentTimeout'));
            finish('timeout');
          } else {
            scheduleNext(poll);
          }
        }
      };

      // ★ 先恢复 token，再开始轮询（解决从支付宝跳回 401 的问题）
      const token = await ensureValidToken();
      if (cancelled) return;

      if (!token) {
        // token 无法恢复 — 登录态不存在或 HttpOnly cookie 已失效
        handle.dismiss();
        toast.error(t('paymentAuthExpired'));
        finish('auth_expired');
        return;
      }
      requestUserId = useAuthStore.getState().user?.id ?? null;
      if (!requestUserId) {
        handle.dismiss();
        toast.error(t('paymentAuthExpired'));
        finish('auth_expired');
        return;
      }

      // 先清理过期订单（用户可能上次未付款就回来了），然后开始轮询
      try {
        await subscriptionsApi.expireStaleOrders();
      } catch {
        // 非关键路径，失败不阻断轮询
      }

      void poll();
    };

    void startPolling();

    return () => {
      cancelled = true;
      terminal = true;
      if (timer) clearTimeout(timer);
      pollingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedOrderId, providedReturnTo, searchParams]);

  // Embedded payment restoration stays invisible in the originating canvas.
  if (!showStatus) return null;
  const hasOrder = Boolean(providedOrderId ?? searchParams.get('order_id'));
  return (
    <main className={`${styles.surface} ${styles.returnPage}`}>
      <section className={styles.returnCard} aria-live="polite" aria-busy={hasOrder}>
        {hasOrder && <SystemLoadingSpinner size="sm" />}
        <h1>{t(hasOrder ? 'paymentProcessing' : 'paymentOrderUnavailable')}</h1>
        <StudioAction variant="primary" href="/explore" className="gap-2">{t('paymentOrder.backToSite')}</StudioAction>
      </section>
    </main>
  );
}
